import { Location, PopStateEvent } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import { blazeActions, RuntimeMode } from '@blaze-shared/app-state-data-access';
import { RuntimePage } from '@blaze-shared/elements-domain';
import { syncSelect } from '@blaze-shared/platform-data-access';

@Injectable({
  providedIn: 'root',
})
export class HistoryNavigationService {
  private readonly location = inject(Location);
  private readonly store = inject(Store);

  private isInitialized = false;

  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    this.isInitialized = true;
    this.initializeHistoryNavigationHandler();
  }

  /**
   * This method handles navigation back/forward in the browser.
   */
  private initializeHistoryNavigationHandler(): void {
    this.location.subscribe(({ url }: PopStateEvent) => {
      if (this.isInEditor() || !url) {
        // Editor handles its own browser navigation.
        return;
      }

      const urlObject = new URL(url, window.location.href);

      // Read data from url
      const { pageSlug } = readDataFromUrl(urlObject);

      // Find target page
      const targetPage = this.getTargetPage(pageSlug);

      if (!targetPage) {
        return;
      }

      // Navigate to page
      const projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);
      this.store.dispatch(
        blazeActions.navigateToPage({
          pageId: targetPage.id,
          projectId,
          runtimePage: targetPage,
        }),
      );
    });
  }

  /**
   * Get the target page by its slug.
   * If slug not provided - go to homepage
   * @param pageSlug
   * @returns
   */
  private getTargetPage(pageSlug: string | undefined): RuntimePage | undefined {
    const runtimePages = syncSelect(this.store, runtimeSelectors.selectRuntimePages);

    // If page slug provided - use it to find the page
    if (pageSlug) {
      const pageIdBySlug = syncSelect(this.store, runtimeSelectors.selectPageIdBySlug(pageSlug));
      return pageIdBySlug ? runtimePages[pageIdBySlug] : undefined;
    }

    // Otherwise (no page slug provided) - try to find the homepage
    // User navigated back to page without a page slug. Find homepage and load it if possible.
    const homepage = Object.values(runtimePages)?.find((page) => page?.isHomePage);

    if (homepage) {
      return runtimePages[homepage.id];
    }

    // Otherwise - no target page
    return undefined;
  }

  private isInEditor(): boolean {
    const runtimeMode = syncSelect(this.store, runtimeSelectors.selectRuntimeMode);

    // Editor handles its own browser navigation.
    return runtimeMode === RuntimeMode.Editor;
  }
}

function readDataFromUrl(urlObject: URL): {
  pageSlug: string | undefined;
  pageDataRowId: string | undefined;
} {
  const [, , /*publishPrefix*/ /*projectName*/ pageSlug, pageDataRowId] = urlObject.pathname
    .split('/')
    .filter((part) => !!part);

  return {
    pageSlug,
    pageDataRowId,
  };
}
